// Force rebuild to pick up category join query
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

import type { BankTxRow, ExpenseRow } from "@/components/finance/banking/reconciliation/types";
import {
  fetchInternalTransfers,
  matchInternalTransfers,
} from "./matchInternalTransfersHelper";

export type ExpenseCandidate = {
  expense_id: string;
  payment_date: string | null;
  expense_date: string;
  net_payment: number | null;
  total_amount: number | null;
  payment_reference: string | null;
  vendor_name: string | null;
  expense_number: string | null;
  category_name: string | null;
  description: string | null;
  score: number;
  reasons: string[];
};

export type BankSuggestion = BankTxRow & {
  candidates: ExpenseCandidate[];
  best_score: number;
  best_reason?: string;
};

type Params = {
  dateFrom: string;
  dateTo: string;
  bankAccountId?: string | null;

  daysWindow?: number;
  amountTolerance?: number;
  maxCandidates?: number;
  pctTolerance?: number;
};

const num = (v: unknown): number | null => {
  if (v == null) return null;
  const n = typeof v === "number" ? v : Number(v);
  return Number.isFinite(n) ? n : null;
};

const dayKey = (isoOrDate: string): string => {
  const d = new Date(isoOrDate);
  if (!Number.isFinite(d.getTime())) return isoOrDate.slice(0, 10);
  return d.toISOString().slice(0, 10);
};

const dayDiff = (aIsoOrDate: string, bIsoOrDate: string): number => {
  const a = new Date(aIsoOrDate);
  const b = new Date(bIsoOrDate);

  if (!Number.isFinite(a.getTime()) || !Number.isFinite(b.getTime())) {
    const aKey = aIsoOrDate.slice(0, 10);
    const bKey = bIsoOrDate.slice(0, 10);
    return Math.round((Date.parse(aKey) - Date.parse(bKey)) / 86400000);
  }

  return Math.round(
    (Date.parse(dayKey(aIsoOrDate)) - Date.parse(dayKey(bIsoOrDate))) / 86400000
  );
};

const normStr = (s: string) =>
  s
    .toLowerCase()
    .replace(/[^\p{L}\p{N}]+/gu, " ")
    .trim();

const tokenOverlap = (a?: string | null, b?: string | null): number => {
  if (!a || !b) return 0;
  const A = new Set(normStr(a).split(" ").filter(Boolean));
  const B = new Set(normStr(b).split(" ").filter(Boolean));
  if (A.size === 0 || B.size === 0) return 0;

  let inter = 0;
  for (const t of A) {
    if (B.has(t)) inter++;
  }

  return inter / Math.max(3, Math.min(A.size, B.size));
};

const extractRefTokens = (s?: string | null): string[] => {
  if (!s) return [];
  // Keep structured references such as TRANSFER-2238050164 intact and ignore
  // generic words like TRANSFER so same-day transfers don't cross-match.
  const up = s.toUpperCase().replace(/[|,;/]+/g, " ");
  const hits = up.match(/[A-Z0-9]+(?:-[A-Z0-9]+)+|[A-Z0-9]{6,32}/g) ?? [];
  return Array.from(new Set(hits.filter((tok) => /\d/.test(tok))));
};

type AmountBest = {
  bestLabel: "bank_amount" | "bank_amount_plus_fee" | "bank_amount_minus_fee";
  bestDelta: number;
};

const bestAmountDelta = (expVals: number[], amount: number, fee: number): AmountBest => {
  const variants = [
    { label: "bank_amount" as const, v: Math.abs(amount) },
    { label: "bank_amount_plus_fee" as const, v: Math.abs(amount) + fee },
    { label: "bank_amount_minus_fee" as const, v: Math.abs(amount) - fee },
  ];

  let best: AmountBest = { bestLabel: "bank_amount", bestDelta: Infinity };

  for (const ev of expVals) {
    for (const vv of variants) {
      const d = Math.abs(ev - vv.v);
      if (d < best.bestDelta) best = { bestLabel: vv.label, bestDelta: d };
    }
  }

  return best;
};

const isAmountPlausible = (
  expVals: number[],
  amount: number,
  fee: number,
  amountTolerance: number,
  pctTolerance: number
) => {
  const absTol = Math.max(amountTolerance * 3, 2);
  const bankAmount = Math.abs(amount);
  const variants = [bankAmount, bankAmount + fee, bankAmount - fee];

  return expVals.some((ev) =>
    variants.some((vv) => {
      const delta = Math.abs(ev - vv);
      const relTol = Math.max(absTol, Math.abs(ev) * pctTolerance);
      return delta <= relTol;
    })
  );
};

const scoreAmount = (
  exp: { net_payment: number | null; total_amount: number | null; currency: string | null },
  tx: { transaction_amount: number | null; transaction_fee: number | null; transaction_amount_currency: string | null },
  tol: number,
  pctTol: number
) => {
  const reasons: string[] = [];
  const eVals = [exp.net_payment, exp.total_amount].filter((x): x is number => x != null);

  const a = tx.transaction_amount;
  const f = tx.transaction_fee ?? 0;

  if (eVals.length === 0 || a == null) return { score: 0, reasons: ["no_amount"] };

  const { bestLabel, bestDelta } = bestAmountDelta(eVals, a, f);

  const absTol = Math.max(tol * 3, 2);
  const relTol = Math.max(absTol, Math.max(...eVals.map((ev) => Math.abs(ev) * pctTol)));

  let s = 0;
  if (bestDelta <= Math.max(tol, 1)) s = 60;
  else if (bestDelta <= relTol) s = 50;
  else if (bestDelta <= relTol * 2) s = 30;
  else s = 0;

  reasons.push(`amount_best=${bestLabel}`);
  reasons.push(`amount_delta=${bestDelta.toFixed(2)}`);

  const c1 = exp.currency;
  const c2 = tx.transaction_amount_currency;
  if (c1 && c2 && c1 === c2) {
    s += 10;
    reasons.push("currency_match");
  } else if (c1 && c2 && c1 !== c2) {
    s -= 10;
    reasons.push("currency_mismatch");
  }

  return { score: Math.max(0, Math.min(70, s)), reasons };
};

const scoreDate = (expensePaidDate: string, txDate: string, daysWindow: number) => {
  const reasons: string[] = [];
  const diff = Math.abs(dayDiff(txDate, expensePaidDate));

  let s = 0;
  if (diff === 0) s = 25;
  else if (diff === 1) s = 20;
  else if (diff <= daysWindow) s = 10;
  else s = 0;

  reasons.push(`date_diff_days=${diff}`);
  return { score: s, reasons };
};

const scoreText = (exp: ExpenseRow, tx: BankTxRow) => {
  const ov = tokenOverlap(exp.vendor_name ?? exp.description, tx.description_details);
  const s = Math.round(Math.min(5, ov * 5));
  const reasons = s > 0 ? [`text_overlap=${ov.toFixed(2)}`] : [];
  return { score: s, reasons };
};

const normType = (v: unknown) => String(v ?? "").trim().toLowerCase();
const isDebitTx = (t: any) => normType(t.transaction_type) === "debit";

type SolvedPair = {
  expense_id: string;
  bank_transaction_id: string;
  score: 100 | 99;
  kind: "ref_exact" | "auto_exact" | "ref_exact_bulk";
  reasons: string[];
};

export function useUnmatchedBankTransactions(params: Params) {
  const {
    dateFrom,
    dateTo,
    bankAccountId,
    daysWindow = 3,
    amountTolerance = 1,
    maxCandidates = 5,
    pctTolerance = 0.02,
  } = params;

  return useQuery({
    queryKey: [
      "unmatched-bank-transactions-v1",
      {
        dateFrom,
        dateTo,
        bankAccountId,
        daysWindow,
        amountTolerance,
        maxCandidates,
        pctTolerance,
      },
    ],
    queryFn: async () => {
      let expQ = supabase
        .from("expenses")
        .select("*, category:expense_categories(name)")
        .gte("payment_date", dateFrom)
        .lte("payment_date", dateTo)
        .in("payment_status", ["paid", "Paid", "PAID"]);

      if (bankAccountId) expQ = expQ.eq("bank_account_id", bankAccountId);
      expQ = expQ.limit(10000);

      const { data: expensesRaw, error: expErr } = await expQ;
      if (expErr) throw expErr;

      const tsFrom = `${dateFrom}T00:00:00+03:00`;

      const toPlus1 = new Date(`${dateTo}T00:00:00+03:00`);
      toPlus1.setUTCDate(toPlus1.getUTCDate() + 1);
      const tsToPlus1 = toPlus1.toISOString();

      let txStrictQ = supabase
        .from("bank_transactions")
        .select("*")
        .gte("transaction_date", tsFrom)
        .lt("transaction_date", tsToPlus1);

      if (bankAccountId) txStrictQ = txStrictQ.eq("bank_account_id", bankAccountId);
      txStrictQ = txStrictQ.limit(10000);

      const { data: txStrictRaw, error: txStrictErr } = await txStrictQ;
      if (txStrictErr) throw txStrictErr;

      const fromExt = new Date(tsFrom);
      fromExt.setUTCDate(fromExt.getUTCDate() - daysWindow);

      const toExt = new Date(tsToPlus1);
      toExt.setUTCDate(toExt.getUTCDate() + daysWindow);

      let txExtQ = supabase
        .from("bank_transactions")
        .select("*")
        .gte("transaction_date", fromExt.toISOString())
        .lt("transaction_date", toExt.toISOString());

      if (bankAccountId) txExtQ = txExtQ.eq("bank_account_id", bankAccountId);
      txExtQ = txExtQ.limit(10000);

      const { data: txExtRaw, error: txExtErr } = await txExtQ;
      if (txExtErr) throw txExtErr;

      const expenses = (expensesRaw ?? []) as ExpenseRow[];
      const txsStrict = (txStrictRaw ?? []) as BankTxRow[];
      const txsExt = (txExtRaw ?? []) as BankTxRow[];

      const expN = expenses.map((e) => ({
        ...e,
        total_amount: num((e as any).total_amount ?? (e as any).expense_total),
        net_payment: num((e as any).net_payment),
      }));

      const txStrictN = txsStrict.map((t) => ({
        ...t,
        transaction_amount: num((t as any).transaction_amount),
        transaction_fee: num((t as any).transaction_fee),
      }));

      const txExtN = txsExt.map((t) => ({
        ...t,
        transaction_amount: num((t as any).transaction_amount),
        transaction_fee: num((t as any).transaction_fee),
      }));

      const txStrictDebit = txStrictN.filter(isDebitTx);
      const txExtDebit = txExtN.filter(isDebitTx);

      const strictTxIds = new Set(txStrictDebit.map((t: any) => t.id));
      const paidDateOf = (e: ExpenseRow) => (e.payment_date ?? e.expense_date) as string;

      const solved: SolvedPair[] = [];
      const lockedExpenses = new Set<string>();
      const lockedTx = new Set<string>();

      const txByRefToken = new Map<string, BankTxRow[]>();

      for (const t of txExtDebit) {
        const tokens = extractRefTokens(t.transaction_id_reference);
        for (const tok of tokens) {
          const arr = txByRefToken.get(tok) ?? [];
          arr.push(t);
          txByRefToken.set(tok, arr);
        }
      }

      // Tier 0: bulk ref-exact (N expenses sharing one reference -> 1 bank debit)
      {
        const expByRefToken = new Map<string, ExpenseRow[]>();
        for (const e of expN) {
          const refValue =
            (e as any).payment_reference ?? (e as any).reference_number ?? null;
          const tokens = extractRefTokens(refValue);
          for (const tok of tokens) {
            const arr = expByRefToken.get(tok) ?? [];
            arr.push(e);
            expByRefToken.set(tok, arr);
          }
        }

        for (const [tok, group] of expByRefToken.entries()) {
          if (group.length < 2) continue;
          const txCands = (txByRefToken.get(tok) ?? []).filter(
            (t) => !lockedTx.has((t as any).id)
          );
          if (!txCands.length) continue;

          const accCount = new Map<string, number>();
          for (const e of group) {
            const acc = e.bank_account_id;
            if (!acc) continue;
            accCount.set(acc, (accCount.get(acc) ?? 0) + 1);
          }
          let dominantAcc: string | null = null;
          let maxN = 0;
          for (const [acc, n] of accCount.entries()) {
            if (n > maxN) {
              maxN = n;
              dominantAcc = acc;
            }
          }
          const groupForAcc = group.filter(
            (e) => e.bank_account_id === dominantAcc &&
              !lockedExpenses.has((e as any).id)
          );
          const txForAcc = txCands.filter((t) => t.bank_account_id === dominantAcc);
          if (groupForAcc.length < 2 || !txForAcc.length) continue;

          const sumTotal = groupForAcc.reduce(
            (acc, e) => acc + (num((e as any).total_amount) ?? 0),
            0
          );
          const sumNet = groupForAcc.reduce(
            (acc, e) =>
              acc + (num((e as any).net_payment) ?? num((e as any).total_amount) ?? 0),
            0
          );
          if (sumTotal <= 0 && sumNet <= 0) continue;

          const bulkAbsTol = Math.max(amountTolerance * 5, 5);
          let bestTx: BankTxRow | null = null;
          let bestDelta = Infinity;
          for (const t of txForAcc) {
            const a = num((t as any).transaction_amount);
            if (a == null) continue;
            const fee = num((t as any).transaction_fee) ?? 0;
            const variants = [Math.abs(a), Math.abs(a) + fee, Math.abs(a) - fee];
            for (const ev of [sumTotal, sumNet]) {
              if (ev <= 0) continue;
              const relTol = Math.max(bulkAbsTol, ev * pctTolerance);
              for (const v of variants) {
                const d = Math.abs(ev - v);
                if (d <= relTol && d < bestDelta) {
                  bestDelta = d;
                  bestTx = t;
                }
              }
            }
          }
          if (!bestTx) continue;

          const tId = (bestTx as any).id as string;
          for (const e of groupForAcc) {
            const eId = (e as any).id as string;
            if (lockedExpenses.has(eId)) continue;
            solved.push({
              expense_id: eId,
              bank_transaction_id: tId,
              score: 100,
              kind: "ref_exact_bulk",
              reasons: [
                `bulk_ref_match group_size=${groupForAcc.length} delta=${bestDelta.toFixed(2)}`,
              ],
            });
            lockedExpenses.add(eId);
          }
          lockedTx.add(tId);
        }
      }

      for (const e of expN) {
        const eId = (e as any).id as string;
        if (lockedExpenses.has(eId)) continue;

        const expenseRefValue =
          (e as any).payment_reference ??
          (e as any).reference_number ??
          null;

        const eTokens = extractRefTokens(expenseRefValue);
        if (!eTokens.length) continue;

        const seen = new Set<string>();
        const candidates: BankTxRow[] = [];

        for (const tok of eTokens) {
          const list = txByRefToken.get(tok);
          if (!list?.length) continue;

          for (const t of list) {
            const tId = (t as any).id as string;
            if (seen.has(tId)) continue;
            seen.add(tId);
            candidates.push(t);
          }
        }

        const pool = candidates
          .filter((t) => !lockedTx.has((t as any).id))
          .filter((t) => (!e.bank_account_id ? true : e.bank_account_id === t.bank_account_id));

        if (!pool.length) continue;

        pool.sort(
          (a, b) =>
            Math.abs(dayDiff(a.transaction_date, paidDateOf(e))) -
            Math.abs(dayDiff(b.transaction_date, paidDateOf(e)))
        );

        const t = pool[0];

        solved.push({
          expense_id: eId,
          bank_transaction_id: (t as any).id,
          score: 100,
          kind: "ref_exact",
          reasons: ["Reference id match"],
        });

        lockedExpenses.add(eId);
        lockedTx.add((t as any).id);
      }

      type Pair = { expense: ExpenseRow; tx: BankTxRow; reasons: string[] };
      const autoPairs: Pair[] = [];

      for (const e of expN) {
        const eId = (e as any).id as string;
        if (lockedExpenses.has(eId)) continue;

        const paidDate = paidDateOf(e);
        const expVals = [num((e as any).net_payment), num((e as any).total_amount)].filter(
          (x): x is number => x != null
        );
        if (!expVals.length) continue;

        for (const t of txExtDebit) {
          const tId = (t as any).id as string;
          if (lockedTx.has(tId)) continue;
          if (e.bank_account_id && e.bank_account_id !== t.bank_account_id) continue;

          const dd = Math.abs(dayDiff(t.transaction_date, paidDate));
          if (dd > 1) continue;

          const a = num((t as any).transaction_amount);
          if (a == null) continue;
          const fee = num((t as any).transaction_fee) ?? 0;

          if (!isAmountPlausible(expVals, a, fee, amountTolerance, pctTolerance)) continue;

          const amtBest = bestAmountDelta(expVals, a, fee);

          autoPairs.push({
            expense: e,
            tx: t,
            reasons: [
              "auto_exact",
              `date_diff_days=${dd}`,
              `amount_best=${amtBest.bestLabel}`,
              `amount_delta=${amtBest.bestDelta.toFixed(2)}`,
            ],
          });
        }
      }

      autoPairs.sort((p1, p2) => {
        const e1 = p1.expense as any;
        const e2 = p2.expense as any;
        const t1 = p1.tx as any;
        const t2 = p2.tx as any;

        const paid1 = paidDateOf(e1);
        const paid2 = paidDateOf(e2);

        const expVals1 = [num(e1.net_payment), num(e1.total_amount)].filter((x): x is number => x != null);
        const expVals2 = [num(e2.net_payment), num(e2.total_amount)].filter((x): x is number => x != null);

        const d1 = bestAmountDelta(expVals1, num(t1.transaction_amount) ?? 0, num(t1.transaction_fee) ?? 0).bestDelta;
        const d2 = bestAmountDelta(expVals2, num(t2.transaction_amount) ?? 0, num(t2.transaction_fee) ?? 0).bestDelta;

        if (d1 !== d2) return d1 - d2;

        const dd1 = Math.abs(dayDiff(t1.transaction_date, paid1));
        const dd2 = Math.abs(dayDiff(t2.transaction_date, paid2));
        return dd1 - dd2;
      });

      for (const p of autoPairs) {
        const eId = (p.expense as any).id as string;
        const tId = (p.tx as any).id as string;
        if (lockedExpenses.has(eId) || lockedTx.has(tId)) continue;

        solved.push({
          expense_id: eId,
          bank_transaction_id: tId,
          score: 99,
          kind: "auto_exact",
          reasons: p.reasons,
        });

        lockedExpenses.add(eId);
        lockedTx.add(tId);
      }

      // Tier 3: internal transfers — match remaining bank tx against internal_transfers.
      // Pull both debit + credit bank tx in the strict window so transfers on either side
      // get locked out of the Unmatched Bank tab.
      try {
        const transfers = await fetchInternalTransfers({
          dateFrom,
          dateTo,
          daysWindow,
          bankAccountId: bankAccountId ?? null,
        });

        const transferPairs = matchInternalTransfers({
          transfers,
          bankTxs: txStrictN as any,
          daysWindow,
          amountTolerance,
          pctTolerance,
          lockedTx,
        });

        for (const p of transferPairs) {
          lockedTx.add(p.bank_transaction_id);
        }
      } catch (err) {
        // Don't fail the whole query if transfers fetch hiccups; just log.
        console.warn("internal_transfers matching failed:", err);
      }

      const remainingExpenses = expN.filter((e) => !lockedExpenses.has((e as any).id));
      const remainingTxStrict = txStrictDebit.filter((t) => !lockedTx.has((t as any).id));

      const unmatchedBank: BankSuggestion[] = remainingTxStrict.map((t) => {
        const candidates = remainingExpenses
          .filter((e) => {
            if (e.bank_account_id && e.bank_account_id !== t.bank_account_id) return false;

            const dd = Math.abs(dayDiff(t.transaction_date, paidDateOf(e)));
            if (dd > daysWindow) return false;

            const expVals = [num((e as any).net_payment), num((e as any).total_amount)].filter(
              (x): x is number => x != null
            );

            const a = num((t as any).transaction_amount);
            const fee = num((t as any).transaction_fee) ?? 0;
            if (!a || !expVals.length) return false;

            return isAmountPlausible(expVals, a, fee, amountTolerance, pctTolerance);
          })
          .map((e) => {
            const paidDate = paidDateOf(e);

            const a = scoreAmount(
              {
                net_payment: num((e as any).net_payment),
                total_amount: num((e as any).total_amount),
                currency: (e as any).currency ?? null,
              },
              {
                transaction_amount: num((t as any).transaction_amount),
                transaction_fee: num((t as any).transaction_fee),
                transaction_amount_currency: (t as any).transaction_amount_currency ?? null,
              },
              amountTolerance,
              pctTolerance
            );

            const d = scoreDate(paidDate, (t as any).transaction_date, daysWindow);
            const x = scoreText(e, t);
            const score = Math.max(0, Math.min(99, a.score + d.score + x.score));

            return {
              expense_id: (e as any).id,
              payment_date: (e as any).payment_date ?? null,
              expense_date: (e as any).expense_date,
              net_payment: num((e as any).net_payment),
              total_amount: num((e as any).total_amount),
              payment_reference: (e as any).payment_reference ?? null,
              vendor_name: (e as any).vendor_name ?? null,
              expense_number: (e as any).expense_number ?? null,
              category_name: (e as any).category?.name ?? null,
              description: (e as any).description ?? null,
              score,
              reasons: [...a.reasons, ...d.reasons, ...x.reasons],
            } satisfies ExpenseCandidate;
          })
          .filter((c) => c.score >= 60)
          .sort((a, b) => b.score - a.score)
          .slice(0, maxCandidates);

        return {
          ...(t as any),
          candidates,
          best_score: candidates[0]?.score ?? 0,
          best_reason: candidates[0]?.reasons?.[0],
        };
      });

      return { solved, unmatchedBank };
    },
    staleTime: 30_000,
  });
}